import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useCurrentAppUser } from './useCurrentAppUser';
import { toast } from 'sonner';

export function useContactLists() {
  const queryClient = useQueryClient();
  const { data: appUser } = useCurrentAppUser();

  const listsQuery = useQuery({
    queryKey: ['contact-lists', appUser?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('contact_lists')
        .select(`
          *,
          item_count:contact_list_items(count)
        `)
        .eq('created_by', appUser.id)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data.map(l => ({
        ...l,
        item_count: l.item_count?.[0]?.count ?? 0,
      }));
    },
    enabled: !!appUser?.id,
  });

  const createList = useMutation({
    mutationFn: async ({ name, description, contactIds = [], dealIds = [] }) => {
      const { data: list, error } = await supabase
        .from('contact_lists')
        .insert({ name, description, created_by: appUser?.id })
        .select()
        .single();
      if (error) throw error;

      await addItemsToList(list.id, contactIds, dealIds);
      return list;
    },
    onSuccess: (list) => {
      queryClient.invalidateQueries({ queryKey: ['contact-lists'] });
      toast.success(`Lista "${list.name}" criada com sucesso.`);
    },
    onError: (err) => toast.error(err?.message || 'Erro ao criar lista.'),
  });

  const addToList = useMutation({
    mutationFn: async ({ listId, contactIds = [], dealIds = [] }) => {
      await addItemsToList(listId, contactIds, dealIds);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contact-lists'] });
      queryClient.invalidateQueries({ queryKey: ['contact-list-items'] });
      toast.success('Itens adicionados à lista.');
    },
    onError: (err) => toast.error(err?.message || 'Erro ao adicionar à lista.'),
  });

  const deleteList = useMutation({
    mutationFn: async (listId) => {
      const { error } = await supabase
        .from('contact_lists')
        .delete()
        .eq('id', listId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contact-lists'] });
      toast.success('Lista excluída.');
    },
    onError: () => toast.error('Erro ao excluir lista.'),
  });

  return {
    lists: listsQuery.data ?? [],
    isLoading: listsQuery.isLoading,
    createList: createList.mutateAsync,
    isCreating: createList.isPending,
    addToList: addToList.mutateAsync,
    isAdding: addToList.isPending,
    deleteList: deleteList.mutateAsync,
  };
}

export function useContactListDetail(listId) {
  const queryClient = useQueryClient();

  const itemsQuery = useQuery({
    queryKey: ['contact-list-items', listId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('contact_list_items')
        .select(`
          *,
          contact:contacts!contact_list_items_contact_id_fkey(id, first_name, last_name, preferred_name, primary_phone, primary_phone_country_code, primary_email, do_not_call),
          deal:deals!contact_list_items_deal_id_fkey(id, contact:contacts!fk_deals_contact(id, first_name, last_name, preferred_name, primary_phone, primary_phone_country_code, primary_email, do_not_call))
        `)
        .eq('list_id', listId)
        .order('added_at', { ascending: false });
      if (error) throw error;
      // Itens de negociação usam o contato da negociação
      return (data || []).map((item) => {
        if (item.contact || !item.deal?.contact) return item;
        return { ...item, contact: item.deal.contact, contact_id: item.deal.contact.id };
      });
    },
    enabled: !!listId,
  });

  const removeItem = useMutation({
    mutationFn: async (itemId) => {
      const { error } = await supabase
        .from('contact_list_items')
        .delete()
        .eq('id', itemId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contact-list-items', listId] });
      queryClient.invalidateQueries({ queryKey: ['contact-lists'] });
    },
    onError: () => toast.error('Erro ao remover item.'),
  });

  const removeItems = useMutation({
    mutationFn: async (itemIds) => {
      if (!itemIds || itemIds.length === 0) return;
      const { error } = await supabase
        .from('contact_list_items')
        .delete()
        .in('id', itemIds);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contact-list-items', listId] });
      queryClient.invalidateQueries({ queryKey: ['contact-lists'] });
      toast.success('Itens removidos da lista.');
    },
    onError: () => toast.error('Erro ao remover itens.'),
  });

  return {
    items: itemsQuery.data ?? [],
    isLoading: itemsQuery.isLoading,
    removeItem: removeItem.mutateAsync,
    removeItems: removeItems.mutateAsync,
    isRemoving: removeItems.isPending,
  };
}

async function addItemsToList(listId, contactIds, dealIds) {
  if (contactIds.length === 0 && dealIds.length === 0) return;

  const uniqueContactIds = [...new Set(contactIds)];
  const uniqueDealIds = [...new Set(dealIds)];

  // Busca apenas os IDs sendo inseridos para checar duplicatas (evita full table scan)
  const filters = [];
  if (uniqueContactIds.length > 0) filters.push(`contact_id.in.(${uniqueContactIds.join(',')})`);
  if (uniqueDealIds.length > 0) filters.push(`deal_id.in.(${uniqueDealIds.join(',')})`);

  const { data: existing } = await supabase
    .from('contact_list_items')
    .select('contact_id, deal_id')
    .eq('list_id', listId)
    .or(filters.join(','));

  const existingContactIds = new Set((existing || []).map(e => e.contact_id).filter(Boolean));
  const existingDealIds = new Set((existing || []).map(e => e.deal_id).filter(Boolean));

  const rows = [
    ...uniqueContactIds.filter(id => !existingContactIds.has(id)).map(id => ({ list_id: listId, contact_id: id })),
    ...uniqueDealIds.filter(id => !existingDealIds.has(id)).map(id => ({ list_id: listId, deal_id: id })),
  ];

  if (rows.length === 0) return;

  if (rows.length > 200) {
    throw new Error(`Limite de 200 itens por vez. Você selecionou ${rows.length}. Reduza a seleção e tente novamente.`);
  }

  const BATCH_SIZE = 100;
  const insertedIds = [];

  try {
    for (let i = 0; i < rows.length; i += BATCH_SIZE) {
      const batch = rows.slice(i, i + BATCH_SIZE);
      const { data, error } = await supabase
        .from('contact_list_items')
        .insert(batch)
        .select('id');
      if (error) throw error;
      insertedIds.push(...data.map(r => r.id));
    }
  } catch (err) {
    // Rollback: remove itens já inseridos nesta operação
    if (insertedIds.length > 0) {
      await supabase.from('contact_list_items')
        .delete()
        .in('id', insertedIds);
    }
    throw err;
  }
}
